/**
 * Binary to Text Tool
 *
 * Convert binary string back to text. Auto-detects separator.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const BinaryToTextTool: ITransformerTool = {
  id: 'binary-to-text',
  name: 'Binary → Text',
  description: 'Convert binary string back to text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['binary', 'text', 'convert', 'decode', 'bits'],

  seo: {
    slug: 'binary-to-text',
    pageTitle: 'Binary to Text Converter - Free Online Tool',
    metaDescription: 'Convert binary strings back to readable text. Auto-detects separators and bit width. Fast, private, runs entirely in your browser.',
    keywords: ['binary to text', 'binary decoder', 'binary to ascii', 'bits to text', 'binary converter'],
    relatedTools: ['text-to-binary', 'hex-decode', 'base64-decode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return '';

    // Auto-detect separator: split by spaces, commas, or treat as continuous
    let chunks: string[];
    if (text.includes(' ')) {
      chunks = text.split(/\s+/);
    } else if (text.includes(',')) {
      chunks = text.split(',').map(c => c.trim());
    } else {
      // No separator — split into 8-bit chunks (or 7-bit if all are 7 chars)
      const bitWidth = text.length % 7 === 0 && text.length % 8 !== 0 ? 7 : 8;
      chunks = [];
      for (let i = 0; i < text.length; i += bitWidth) {
        chunks.push(text.slice(i, i + bitWidth));
      }
    }

    // Validate and convert
    const bytes: number[] = [];
    for (const chunk of chunks) {
      const cleaned = chunk.trim();
      if (!cleaned) continue;
      if (!/^[01]+$/.test(cleaned)) {
        throw ToolError.validation(`Invalid binary character in "${cleaned}" — only 0 and 1 are allowed`);
      }
      const value = parseInt(cleaned, 2);
      if (value > 255) {
        throw ToolError.validation(`Binary value "${cleaned}" exceeds one byte (255)`);
      }
      bytes.push(value);
    }

    const decoder = new TextDecoder();
    return decoder.decode(new Uint8Array(bytes));
  },
};
